import Component from '@glimmer/component';
import { gte } from 'tio-ui/utilities';

// ---------------------------------------------------------------------------
// ActionableCard – card with badges, match score, skills, and actions
// ---------------------------------------------------------------------------

interface ActionableCardSignature {
  Args: {
    title?: string;
    matchScore?: number;
    showBanner?: boolean;
    bannerText?: string;
    bannerClass?: string;
    cardClass?: string;
  };
  Blocks: {
    banner?: [];
    badge?: [];
    header?: [];
    metadata?: [];
    skills?: [];
    eligibility?: [];
    actions?: [];
    default?: [];
  };
  Element: HTMLDivElement;
}

/**
 * A flexible card component for displaying actionable items with optional
 * banner, match score, skill chips, eligibility badges, and action buttons.
 *
 * @example
 * ```gts
 * import ActionableCard from 'tio-reimagined/components/actionable-card';
 *
 * <ActionableCard @title="Data Science Certificate" @matchScore={{92}} @showBanner={{true}} @bannerText="Top Pick">
 *   <:skills>
 *     <SkillChip @name="Python" />
 *   </:skills>
 *   <:actions>
 *     <Button @intent="primary">Apply</Button>
 *   </:actions>
 * </ActionableCard>
 * ```
 */
export default class ActionableCard extends Component<ActionableCardSignature> {
  get bannerClass() {
    return (
      this.args.bannerClass ??
      'bg-gradient-to-r from-violet-500 to-purple-500 text-white text-sm font-medium px-4 py-2 flex items-center gap-2'
    );
  }

  get cardClass() {
    return (
      this.args.cardClass ?? 'bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'
    );
  }

  get hasMatchScore() {
    return this.args.matchScore != null;
  }

  <template>
    <div data-test-actionable-card class={{this.cardClass}} ...attributes>
      {{#if (has-block "banner")}}
        {{yield to="banner"}}
      {{else if @showBanner}}
        <div data-test-actionable-card-banner class={{this.bannerClass}}>
          <svg
            class="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 256 256"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M234.5,114.38l-45.1,39.36,13.51,58.6a16,16,0,0,1-23.84,17.34l-51.11-31-51,31a16,16,0,0,1-23.84-17.34L66.61,153.8,21.5,114.38a16,16,0,0,1,9.11-28.06l59.46-5.15,23.21-55.36a15.95,15.95,0,0,1,29.44,0h0L166,81.17l59.44,5.15a16,16,0,0,1,9.11,28.06Z"
            ></path>
          </svg>
          {{@bannerText}}
        </div>
      {{/if}}

      <div class="p-4">
        {{#if (has-block "header")}}
          {{yield to="header"}}
          {{#if (has-block "metadata")}}
            {{yield to="metadata"}}
          {{/if}}
        {{else if @title}}
          <div class="flex items-start justify-between gap-4 mb-4">
            <div>
              <div class="flex items-center gap-2 mb-2">
                <h4 data-test-actionable-card-title class="font-semibold text-lg">{{@title}}</h4>
                {{#if (has-block "badge")}}
                  {{yield to="badge"}}
                {{/if}}
              </div>
              {{#if (has-block "metadata")}}
                {{yield to="metadata"}}
              {{/if}}
            </div>
            {{#if this.hasMatchScore}}
              <div data-test-actionable-card-score class="text-center">
                <div
                  class="w-14 h-14 rounded-full flex items-center justify-center font-bold ring-2
                    {{if
                      (gte @matchScore 90)
                      'bg-green-100 text-green-700 ring-green-200'
                      (if
                        (gte @matchScore 70)
                        'bg-amber-100 text-amber-700 ring-amber-200'
                        'bg-gray-100 text-gray-700 ring-gray-200'
                      )
                    }}"
                >
                  {{@matchScore}}%
                </div>
                <span class="text-xs text-gray-500 mt-1">Match</span>
              </div>
            {{/if}}
          </div>
        {{/if}}

        {{#if (has-block "skills")}}
          <div class="flex flex-wrap gap-2 mb-4">
            {{yield to="skills"}}
          </div>
        {{/if}}

        {{#if (has-block "eligibility")}}
          <div class="mb-4">
            {{yield to="eligibility"}}
          </div>
        {{/if}}

        {{#if (has-block "actions")}}
          <div class="flex items-center justify-between pt-4 border-t border-gray-100">
            {{yield to="actions"}}
          </div>
        {{/if}}

        {{#if (has-block)}}
          {{yield}}
        {{/if}}
      </div>
    </div>
  </template>
}
